import { call, put } from 'redux-saga/effects';
import ActionTypes from '@ziynx/action-types';
import type { MongoIdType } from '@ziynx/flow-types/globals';

type GetTeamListResponse = {
  isLastPage: boolean,
  page: number,
  pageSize: number,
  total: number,
  results: Array<{
    _id: MongoIdType,
    email: string,
    firstName: string,
    lastName: string,
    role: 'owners' | 'managers' | 'admins',
    userStatus: 'PENDING' | 'EXPIRED' | 'CONFIRMED'
  }>,
};

type PostInviteAction = {
  id: string,
  body: {
    email?: string,
    type?: string,
    firstName?: string,
    lastName?: string,
  },
 };

 type GetInviteAction = {
  token?: string,
  type: string,
 };

 type ResendInviteAction = {
  token?: string,
 };

 type PatchInviteAction = {
  token?: string,
  type?: string,
  body?: [
    {
      op: 'add' | 'replace' | 'remove',
      path: 'firstName' | 'lastName' | 'email' | 'role',
      value: string,
    }
  ]
};

type DeleteInviteAction = {
  token?: string,
  type?: string,
};

type RemoveUserAction = {
  id: MongoIdType,
  memberId: string,
}

export function* getUser(action): Generator<any, void, empty> {
  try {
    const response = yield call(() => APIService.apiRequest('GET_USER', 'GET', {
      routeParams: {
        id: action?.id ?? 'me',
      },
    }));
    yield put({
      type: ActionTypes.teamMember.getUser.SUCCESS,
      id: action?.id ?? 'me',
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.teamMember.getUser.ERROR,
      error,
    });
  }
}

export function* getTeamList(action): Generator<any, void, empty> {
  try {
    const response: GetTeamListResponse = yield call(() => APIService.apiRequest('GET_TEAM_LIST', 'GET', {
      routeParams: {
        id: action?.id ?? 'me',
      },
      query: {
        pageSize: 10,
        page: action?.page ?? 0,
      },
    }));
    yield put({
      type: ActionTypes.team.get.SUCCESS,
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.team.get.ERROR,
      error,
    });
  }
}

export function* getInvite(action: GetInviteAction): Generator<any, void, empty> {
  try {
    const response = yield call(() => APIService.apiRequest('GET_INVITE', 'GET', {
      routeParams: {
        token: action?.token,
      },
      authorization: action?.authorization,
    }));
    yield put({
      type: ActionTypes.teamMember.getInvite.SUCCESS,
      token: action?.token,
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.teamMember.getInvite.ERROR,
      token: action?.token,
      error,
    });
  }
}

export function* postInvite(action: PostInviteAction): Generator<any, void, empty> {
  const actionId = action?.id ?? 'me';
  try {
    const response: {
      MessageId: string,
      companyId: string,
      companyName: string,
      email: string,
      inviteCode: string,
      type: 'owner' | 'manager' | 'admin',
    } = yield call(() => APIService.apiRequest('POST_INVITE', 'POST', {
      routeParams: {
        id: actionId,
      },
      body: action?.body ?? {},
    }));
    yield put({
      type: ActionTypes.teamMember.post.SUCCESS,
      token: action?.token,
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.teamMember.post.ERROR,
      id: actionId,
      error,
    });
  }
}

export function* resendInvite(action: ResendInviteAction): Generator<any, void, empty> {
  try {
    yield call(() => APIService.apiRequest('RESEND_INVITE', 'POST', {
      routeParams: {
        token: action?.token,
      },
    }));
    yield put({
      type: ActionTypes.teamMember.resendInvite.SUCCESS,
      userStatus: 'PENDING',
      memberId: action?.token,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.teamMember.resendInvite.ERROR,
      token: action?.token,
      error,
    });
  }
}

export function* patchInvite(action: PatchInviteAction): Generator<any, void, empty> {
  try {
    const response = yield call(() => global.APIService.apiRequest('PATCH_INVITE', 'PATCH', {
      routeParams: {
        token: action?.token,
      },
      body: action?.body ?? [],
    }));
    yield put({
      type: ActionTypes.teamMember.patchInvite.SUCCESS,
      token: action?.token,
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.teamMember.patchInvite.ERROR,
      token: action?.token,
      error,
    });
  }
}

export function* removeInvite(action: DeleteInviteAction): Generator<any, void, empty> {
  try {
    yield call(() => APIService.apiRequest('REMOVE_INVITE', 'DELETE', {
      routeParams: {
        token: action?.token,
      },
    }));
    yield put({
      type: ActionTypes.teamMember.removeInvite.SUCCESS,
      memberId: action?.token,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.teamMember.removeInvite.ERROR,
      error,
    });
  }
}

export function* patchUser(action): Generator<any, void, empty> {
  const actionId = action?.id ?? 'me';
  try {
    const response = yield call(() => APIService.apiRequest('PATCH_USER', 'PATCH', {
      routeParams: {
        id: actionId,
      },
      body: action?.body ?? [],
    }));
    yield put({
      type: ActionTypes.teamMember.patchUser.SUCCESS,
      id: actionId,
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.teamMember.patchUser.ERROR,
      id: actionId,
      error,
    });
  }
}

export function* removeUser(action: RemoveUserAction): Generator<any, void, empty> {
  const companyId = action?.id ?? 'me';
  try {
    yield call(() => APIService.apiRequest('REMOVE_TEAM_MEMBER', 'DELETE', {
      routeParams: {
        id: companyId,
        userId: action?.memberId,
      },
    }));
    yield put({
      type: ActionTypes.team.removeUser.SUCCESS,
      memberId: action?.memberId,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.team.removeUser.ERROR,
      error,
    });
  }
}
